import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQuery } from './base-query';

const endUserAPI = createApi({
    reducerPath: "endUserAPI",
    baseQuery,
    endpoints: (builder) => ({
        // ====================================================================
        // CUSTOMER MANAGEMENT MUTATIONS
        // ====================================================================
        getAllEndUsers: builder.mutation({
            query: ({ page, size, search, searchBy, status }) => {
                let apiUrl = `customer?page=${page + 1}&size=${size}`;

                if (search) {
                    apiUrl += `&search=${search}`;
                }

                if (searchBy) {
                    apiUrl += `&searchBy=${searchBy}`;
                }

                if (status) {
                    apiUrl += `&status=${status}`;
                }

                return {
                    url: apiUrl,
                    method: "GET",
                };
            },
            transformResponse: (response) => response,
            providesTags: ["endUser"],
        }),

        getAllEndUsersForExport: builder.mutation({
            query: () => {
                let apiUrl = `customer/export`;

                return {
                    url: apiUrl,
                    method: "GET",
                };
            },
            transformResponse: (response) => response
        }),

        getEndUserById: builder.query({
            query: ({ id }) => {
                let apiUrl = `customer/${id}`;

                return {
                    url: apiUrl,
                    method: "GET",
                };
            },
            transformResponse: (response) => response,
            providesTags: (result, error, { id }) => [{ type: 'endUser', id }]
        }),

        createEndUser: builder.mutation({
            query: ({ body }) => ({
                url: `customer`,
                method: "POST",
                body
            }),
            invalidatesTags: ["endUser"],
        }),

        updateEndUser: builder.mutation({
            query: ({ id, body }) => ({
                url: `customer/${id}`,
                method: "PUT",
                body
            }),
            invalidatesTags: ["endUser"],
        }),

        deleteEndUser: builder.mutation({
            query: ({ id }) => ({
                url: `customer/${id}`,
                method: "DELETE",
                body: {}
            }),
            invalidatesTags: ["endUser"],
        }),

        updateEndUserEmail: builder.mutation({
            query: ({ id, body }) => ({
                url: `customer/${id}/change-email`,
                method: "PATCH",
                body
            })
        }),

        updateEndUserMobile: builder.mutation({
            query: ({ id, body }) => ({
                url: `customer/${id}/change-phone`,
                method: "PATCH",
                body
            })
        }),

        deactivateEndUser: builder.mutation({
            query: ({ id, body }) => ({
                url: `customer/${id}/deactivate`,
                method: "POST",
                body
            })
        }),

        activateEndUser: builder.mutation({
            query: ({ id }) => ({
                url: `customer/${id}/reactivate`,
                method: "POST",
                body: {}
            })
        }),

        promoteEndUserAsVendor: builder.mutation({
            query: ({ id, body }) => ({
                url: `customer/${id}/promote`,
                method: "POST",
                body
            })
        }),

        updateEndUserNote: builder.mutation({
            query: ({ id, body }) => ({
                url: `customer/${id}/note`,
                method: "PATCH",
                body
            })
        }),
        // Reviews
        getCustomerReviews: builder.mutation({
        query: ({ customerId, page = 1, size = 10 }) => ({
            url: `reviews/customer/${customerId}?page=${page}&size=${size}`,
            method: 'GET',
        }),
        transformResponse: (response) => response,
        providesTags: ['endUserReviews'],
        }),

        // ====================================================================
        // DOCUMENT MANAGEMENT MUTATIONS
        // ====================================================================

        listDocumentSubmissions: builder.query({
            query: ({ status, page, limit }) => {
                let apiUrl = `/documents/submissions?page=${page + 1}&size=${limit}`;

                if (status) {
                    apiUrl += `&status=${status}`;
                }
                return {
                    url: apiUrl,
                    method: "GET",
                };
            },
            transformResponse: (response) => response,
            providesTags: ["documents"],
            refetchOnMountOrArgChange: true,
        }),

        // Upload document for a person
        uploadDocumentForPerson: builder.mutation({
            query: ({ personId, body }) => ({
                url: `documents/${personId}`,
                method: "POST",
                body
            }),
            invalidatesTags: (result, error, { personId }) => [
                "documents", 
                { type: 'endUser', id: personId }
            ],
        }),

        // ====================================================================
        // SINGLE DOCUMENT VERIFICATION & REJECTION
        // ====================================================================
        verifyDocument: builder.mutation({
            query: ({ documentId }) => ({
                url: `documents/${documentId}/verify`,
                method: "PATCH",
                body: {}
            }),
            invalidatesTags: ["documents", "endUser"],
        }),

        // Reject a single document

        rejectDocument: builder.mutation({
            query: ({ documentId, rejectionReason }) => ({
                url: `documents/${documentId}/reject`,
                method: "PATCH",
                body: { rejectionReason }
            }),
            invalidatesTags: ["documents", "endUser"],
        }),

        // ====================================================================
        // BATCH DOCUMENT VERIFICATION & REJECTION
        // ====================================================================

        batchVerifyDocuments: builder.mutation({
            query: ({ documentIds }) => ({
                url: `documents/batch/verify`,
                method: "PATCH",
                body: { documentIds }
            }),
            invalidatesTags: ["documents", "endUser"],
        }),

        // Batch reject multiple documents

        batchRejectDocuments: builder.mutation({
            query: ({ documentIds, rejectionReason }) => ({
                url: `documents/batch/reject`,
                method: "PATCH",
                body: { documentIds, rejectionReason }
            }),
            invalidatesTags: ["documents", "endUser"],
        }),

        // ====================================================================
        // INSIGHTS
        // ====================================================================
        getEndUserInsights: builder.query({
            query: ({ startDate, endDate }) => {
                let apiUrl = `customer/insights`;
                if (startDate) {
                    apiUrl += `?startDate=${startDate}`;
                }
                if (endDate) {
                    apiUrl += `${startDate ? '&' : '?'}endDate=${endDate}`;
                }
                return {
                    url: apiUrl,
                    method: 'GET',
                };
            },
            transformResponse: (response) => response.data,
            providesTags: ['endUserInsights'],
        }),

    })
});

export const {
    // Customer queries
    useGetAllEndUsersMutation,
    useGetEndUserByIdQuery,
    useGetAllEndUsersForExportMutation,
    useCreateEndUserMutation,
    useUpdateEndUserMutation,
    useDeleteEndUserMutation,
    useUpdateEndUserEmailMutation,
    useUpdateEndUserMobileMutation,
    useDeactivateEndUserMutation,
    useActivateEndUserMutation,
    usePromoteEndUserAsVendorMutation,
    useUpdateEndUserNoteMutation,
    // Reviews
    useGetCustomerReviewsMutation,

    // Document queries & mutations
    useListDocumentSubmissionsQuery,
    useUploadDocumentForPersonMutation,

    // Document mutations - Single Verify/Reject
    useVerifyDocumentMutation,
    useRejectDocumentMutation,

    // Document mutations - Batch Verify/Reject
    useBatchVerifyDocumentsMutation,
    useBatchRejectDocumentsMutation,

    // Insights
    useGetEndUserInsightsQuery,

} = endUserAPI;

export default endUserAPI;